import type { ReactNode, ReactElement } from 'react'
import React, { isValidElement } from 'react'
import styles from './index.module.css'
import { hasDisplayName } from '@s4l/utils/library/react'
import Placeholder from '../../Placeholder'

interface SkeletonProps {
  children: ReactNode
  isLoading?: boolean
}

interface ElementWithProps extends ReactElement {
  props: {
    children?: ReactNode
    [key: string]: unknown
  }
}

const isValidElementWithProps = (element: unknown): element is ElementWithProps => {
  return (
    React.isValidElement(element) && typeof element.props === 'object' && element.props !== null
  )
}

const getElementName = (element: ElementWithProps): string | undefined => {
  if (
    isValidElement(element) &&
    typeof element.type === 'function' &&
    hasDisplayName(element.type)
  ) {
    return element.type.displayName
  }

  if (typeof element.type === 'string') {
    return element.type
  }

  return undefined
}

const renderInputSkeleton = () => (
  <Placeholder height="48px" width="100%" className={styles.inputSkeleton} rounded />
)

const renderTextAreaSkeleton = () => (
  <Placeholder height="96px" width="100%" className={styles.textAreaSkeleton} rounded />
)

const renderCheckboxSkeleton = () => (
  <Placeholder height="20px" width="20px" className={styles.checkboxSkeleton} />
)

const renderLabelSkeleton = () => <Placeholder className={styles.labelSkeleton} rounded />

const renderSliderSkeleton = () => (
  <Placeholder className={styles.sliderSkeleton} height="10px" width="100%" rounded />
)

const cloneElementWithSkeleton = (element: ReactNode): ReactNode => {
  if (!isValidElementWithProps(element)) {
    return element
  }

  const elementName = getElementName(element)

  if (elementName?.includes('Input')) {
    return renderInputSkeleton()
  }

  if (elementName?.includes('TextArea')) {
    return renderTextAreaSkeleton()
  }

  if (elementName?.includes('Checkbox')) {
    return renderCheckboxSkeleton()
  }

  if (elementName?.includes('Collapsible')) {
    return <Placeholder height="24px" width="200px" rounded className="mt-16" />
  }

  if (elementName?.includes('Slider')) {
    return renderSliderSkeleton()
  }

  if (elementName?.includes('Label')) {
    const { children } = element.props
    const processedChildren = children
      ? React.Children.map(children, child => {
          if (!isValidElementWithProps(child)) {
            return cloneElementWithSkeleton(renderLabelSkeleton())
          }
          return cloneElementWithSkeleton(child)
        })
      : null

    return <div className={styles.labelWrapperSkeleton}>{processedChildren}</div>
  }

  const { children } = element.props
  if (!children) {
    return element
  }

  const newChildren = React.Children.map(children, child => {
    if (isValidElementWithProps(child)) {
      return cloneElementWithSkeleton(child)
    }
    return child
  })

  return React.cloneElement(element, {}, newChildren)
}

const Skeleton = ({ children, isLoading = true }: SkeletonProps) => {
  if (!isLoading) {
    return <>{children}</>
  }

  const skeletonChildren = React.Children.map(children, child => {
    if (isValidElementWithProps(child)) {
      return cloneElementWithSkeleton(child)
    }
    return child
  })

  return (
    <div className={styles.container}>
      {skeletonChildren}
      <Placeholder.Animation />
    </div>
  )
}

Skeleton.displayName = 'Form.Skeleton'
export default Skeleton
